import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Notification } from '../utils/interfaces';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications$ = new Subject<Notification>();
  public notifications = this.notifications$.asObservable();

  show(notification: Omit<Notification, 'id'>): string {
    const id = this.generateId();
    const fullNotification: Notification = {
      id,
      ...notification,
      duration: notification.duration ?? 5000 // Default 5 seconds if not provided
    };

    this.notifications$.next(fullNotification);
    return id;
  }

  success(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'success',
      title,
      message,
      duration
    });
  }

  error(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'error',
      title,
      message,
      duration
    });
  }

  warning(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'warning',
      title,
      message,
      duration
    });
  }

  info(title: string, message: string, duration?: number): string {
    return this.show({
      type: 'info',
      title,
      message,
      duration
    });
  }

  sessionExpiring(minutesLeft: number, onExtend?: () => void): string {
    return this.show({
      type: 'warning',
      title: 'Session Expiring Soon',
      message: `Your session will expire in ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}. Save your work!`,
      duration: 30000, // 30 seconds
      actions: onExtend ? [{
        label: 'Extend Session',
        action: onExtend
      }] : undefined
    });
  }

  sessionExpired(onLogin?: () => void): string {
    return this.show({
      type: 'error',
      title: 'Session Expired',
      message: 'Your session has expired. Please log in again to continue.',
      duration: 10000, // 10 seconds
      actions: onLogin ? [{
        label: 'Log In',
        action: onLogin
      }] : undefined
    });
  }

  authenticationRequired(message?: string): string {
    return this.show({
      type: 'warning',
      title: 'Authentication Required',
      message: message || 'Please log in to access this resource.',
      duration: 5000
    });
  }

  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}